/**
 * TASK EDIT MODAL BODY — Corpo della modale di modifica, con lo stesso layout
 * della modale di creazione (titolo eroe + pill etichettate + "Più opzioni" +
 * associazioni), per uniformare le due schermate.
 *
 * Differenza chiave rispetto alla creazione: qui NON c'è submit. Ogni controllo
 * salva subito (dispatch updateTask), come il vecchio TaskEditFormBody — che
 * resta in uso per l'embed nella timeline (ActivityTimeline), a layout compatto.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useServerPagedSearch, useLazyResolution, datetimeLocalToIso } from '@crm/shared';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchPipelines } from '../pipeline/pipelineThunks';
import { pipelinesOfType } from '../pipeline/pipelineEntityTypes';
import { findOwningPipeline, pipelineStateLabel, sortedStates } from '../pipeline/pipelineUtils';
import type { PipelineSliceState } from '../pipeline/pipelineTypes';
import { useCurrentGroupId } from '../../auth/authSelectors';
import { useToast } from '../../components/useToast';
import { RichTextField } from '../../components/RichTextField';
import { ActivityTools } from '../../components/ActivityTools';
import { useAssociatedRecordsBinder } from '../../components/useAssociatedRecordsBinder';
import { getEntityFqn } from '../../entityRegistry/entityRegistry';
import { userApi } from '../user/userApi';
import { fetchQueues } from '../queue/queueThunks';
import { updateTask, updateTaskStatus } from './taskThunks';
import { PillField, PillOptions } from './PillField';
import { TagGlyph, CalendarGlyph, ClockGlyph, FlagGlyph, ChevronDownGlyph, CheckGlyph } from '../../components/icons';
import { TASK_TYPES, TASK_PRIORITIES, type Task, type TaskCreatePayload, type TaskUpdatePayload, type TaskType, type TaskPriority } from './taskTypes';
import { TASK_RECURRENT_UNITS, type TaskRecurrentUnit } from './taskRecurrentUnits';
import type { User } from '../user/userTypes';

const PRIO_DOT: Record<string, string> = { high: 'high', medium: 'med', low: 'low', none: 'low' };

const TIME_OPTIONS: string[] = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, '0');
  return `${h}:${i % 2 === 0 ? '00' : '30'}`;
});

interface AssignableUser { id: number; fullName: string; }

/** ISO → parti locali {date: YYYY-MM-DD, time: HH:mm} ('' se null). */
function isoToLocalParts(iso: string | null): { date: string; time: string } {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

/** Ricompone l'ISO da data + ora (ora di default 17:00 se assente). */
function combineToIso(date: string, time: string): string | null {
  if (!date) return null;
  const hhmm = /^\d{2}:\d{2}$/.test(time) ? time : '17:00';
  return datetimeLocalToIso(`${date}T${hhmm}`);
}

/** Data locale odierna YYYY-MM-DD (per l'Orario impostato senza una scadenza). */
function todayDateStr(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function TaskEditModalBody({ task }: { task: Task }) {
  const dispatch = useAppDispatch();

  // Le pipeline DI TIPO TASK: quelle dei preventivi porterebbero stati che il task non puo' avere.
  const allPipelines = useAppSelector((st) => (st.pipelines as PipelineSliceState).items);
  const taskPipelines = useMemo(
    () => pipelinesOfType(allPipelines, getEntityFqn('Task')),
    [allPipelines],
  );

  // Senza questo carico il blocco non comparirebbe affatto quando nessun'altra schermata ha gia'
  // letto le pipeline: niente errore, solo due campi assenti.
  useEffect(() => {
    if (allPipelines.length === 0) dispatch(fetchPipelines({ pageSize: 1000 }));
  }, [allPipelines.length, dispatch]);

  // La pipeline di partenza e' quella che possiede lo stato attuale del task. Non e' un campo
  // salvato: serve solo a restringere la seconda tendina.
  const [pipelineId, setPipelineId] = useState(0);
  const owningPipelineId = findOwningPipeline(taskPipelines, task.currentStateId)?.pipelineId ?? 0;

  useEffect(() => {
    if (owningPipelineId) setPipelineId(owningPipelineId);
  }, [owningPipelineId]);

  const pipelineStates = useMemo(
    () => sortedStates(taskPipelines.find((pl) => pl.pipelineId === pipelineId)),
    [taskPipelines, pipelineId],
  );

  const toast = useToast();
  const { t, i18n } = useTranslation();
  const groupId = useCurrentGroupId();

  // Titolo: controllato in locale, commit su blur/Enter (resta un "eroe").
  const [title, setTitle] = useState(task.title);

  async function saveTaskPatch(patch: Partial<TaskUpdatePayload>) {
    try {
      await dispatch(updateTask({ taskId: task.taskId, ...patch })).unwrap();
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }

  const commitField = (field: keyof TaskCreatePayload) => (value: unknown) =>
    saveTaskPatch({ [field]: value } as Partial<TaskUpdatePayload>);

  function commitTitle() {
    const next = title.trim();
    if (next && next !== task.title) saveTaskPatch({ title: next });
  }

  // Pallino di stato accanto al titolo: click = concludi (toggle completato).
  const done = task.status === 'completed';
  async function toggleCompleted() {
    try {
      await dispatch(updateTaskStatus({ taskId: task.taskId, status: done ? 'todo' : 'completed' })).unwrap();
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }

  // Code: elenco completo (come la creazione) per poter cambiare valore.
  const { items: queues } = useAppSelector((state) => state.queues);
  useEffect(() => { dispatch(fetchQueues()); }, [dispatch]);

  // Assegnatario: risoluzione nome + lookup.
  const currentUser = useLazyResolution<User, AssignableUser>(
    task.assignedUserId,
    (id) => userApi.getById(id, groupId),
    (u) => ({ id: u.id, fullName: u.fullName }),
  );
  const {
    loading: usersLoading,
    autocompleteProps: userAutocompleteProps,
    listboxProps: userListboxProps,
  } = useServerPagedSearch<AssignableUser>({
    fetchPage: async (q, page, size) => {
      const list = await userApi.search(q, groupId);
      const start = (page - 1) * size;
      return {
        items: list.slice(start, start + size).map((u: User) => ({ id: u.id, fullName: u.fullName })),
        totalCount: list.length,
      };
    },
    pageSize: 20,
    debounceMs: 300,
    resetOn: [groupId],
  });
  const handleUserChange = useCallback(
    async (newUser: AssignableUser | null) => {
      if (!newUser || newUser.id === task.assignedUserId) return;
      await saveTaskPatch({ assignedUserId: newUser.id });
    },
    // saveTaskPatch è stabile per la durata del task; dipendenza sul solo id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [task.assignedUserId, task.taskId],
  );

  // Associazioni + commenti (come nel vecchio body): binder edit mode.
  const taskFqn = getEntityFqn('Task');
  const {
    records: currentlyLinked,
    onRecordsChange: handleRecordsChange,
    recordsSaving,
    pickerProps: recordsPickerProps,
  } = useAssociatedRecordsBinder({ mode: 'edit', entityFqn: taskFqn ?? '', entityId: task.taskId });

  // Ricorrenza (i tre campi viaggiano insieme).
  function handleRecurrentUnitChange(unit: TaskRecurrentUnit | 'none') {
    if (unit === 'none') {
      saveTaskPatch({ recurrent: false, recurrentUnit: 'none', recurrentValue: 0 });
    } else {
      saveTaskPatch({ recurrent: true, recurrentUnit: unit, recurrentValue: task.recurrentValue > 0 ? task.recurrentValue : 1 });
    }
  }

  // Scadenza / Orario derivati dall'ISO persistito.
  const { date: deadlineDate, time: deadlineTime } = useMemo(() => isoToLocalParts(task.expirationDate), [task.expirationDate]);
  const deadlineDisplay = useMemo(() => {
    if (!task.expirationDate) return '—';
    return new Date(task.expirationDate).toLocaleDateString(i18n.language.replace(/_/g, '-'), {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  }, [task.expirationDate, i18n.language]);

  // Etichette pill.
  const typeLabel = t(TASK_TYPES.find((x) => x.value === task.type)?.label ?? '');
  const prioEntry = TASK_PRIORITIES.find((x) => x.value === task.priority);
  const prioLabel = prioEntry ? t(prioEntry.label) : '';

  return (
    <div className="task-create">
      <div className="task-create__titlerow">
        <button
          type="button"
          className={`tcheck${done ? ' done' : ''}`}
          role="checkbox"
          aria-checked={done}
          aria-label={t(`task.statuses.${done ? 'completed' : 'todo'}`)}
          onClick={toggleCompleted}
        >
          <CheckGlyph />
        </button>
        <input
          className="task-create__title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={commitTitle}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); (e.currentTarget as HTMLInputElement).blur(); } }}
          placeholder={t('task.taskTitle')}
          aria-label={t('task.taskTitle')}
          required
        />
      </div>

      <div className="task-create__quick">
        <PillField icon={<TagGlyph />} label={t('task.taskType')} value={typeLabel}>
          {(close) => (
            <PillOptions
              options={TASK_TYPES.map((x) => ({ value: x.value, label: t(x.label) }))}
              selected={task.type}
              onSelect={(v) => commitField('type')(v as TaskType)}
              close={close}
            />
          )}
        </PillField>

        <PillField icon={<CalendarGlyph />} label={t('task.expirationDate')} value={deadlineDisplay} popoverWidth={200}>
          {() => (
            <TextField
              type="date" value={deadlineDate} aria-label={t('task.expirationDate')}
              onChange={(e) => commitField('expirationDate')(combineToIso(e.target.value, deadlineTime))}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth size="small" autoFocus
            />
          )}
        </PillField>

        <PillField icon={<ClockGlyph />} label={t('task.deadlineTime')} value={deadlineTime || '—'} popoverWidth={130}>
          {(close) => (
            <PillOptions
              options={(TIME_OPTIONS.includes(deadlineTime) || !deadlineTime ? TIME_OPTIONS : [deadlineTime, ...TIME_OPTIONS])
                .map((tm) => ({ value: tm, label: tm }))}
              selected={deadlineTime}
              onSelect={(v) => commitField('expirationDate')(combineToIso(deadlineDate || todayDateStr(), v))}
              close={close}
            />
          )}
        </PillField>

        <PillField
          icon={<FlagGlyph />} label={t('task.priority')}
          value={<><i className={`pdot ${PRIO_DOT[task.priority] ?? 'med'}`} />{prioLabel}</>}
        >
          {(close) => (
            <PillOptions
              options={TASK_PRIORITIES.map((p) => ({
                value: p.value,
                label: <span className="tf-prio-opt"><i className={`pdot ${PRIO_DOT[p.value] ?? 'med'}`} />{t(p.label)}</span>,
              }))}
              selected={task.priority}
              onSelect={(v) => commitField('priority')(v as TaskPriority)}
              close={close}
            />
          )}
        </PillField>

        <PillField
          icon={<span className="tf-av">{(currentUser?.fullName ?? '?').slice(0, 1).toUpperCase()}</span>}
          label={t('task.assignedTo')}
          value={currentUser?.fullName ?? '—'}
          popoverWidth={280}
        >
          {(close) => (
            <Autocomplete
              {...userAutocompleteProps}
              value={currentUser}
              onChange={(_, val) => { handleUserChange(val); close(); }}
              getOptionLabel={(o) => o.fullName}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              loading={usersLoading}
              openOnFocus
              slotProps={{ listbox: userListboxProps }}
              renderInput={(params) => <TextField {...params} label={t('task.assignedTo')} autoFocus />}
              size="small"
            />
          )}
        </PillField>
      </div>

      <details className="task-create__more">
        <summary><ChevronDownGlyph className="chv" /> {t('task.moreOptions')}</summary>
        <div className="task-create__more-body">
          <div className="tc-row2">
            <div className="fld">
              <label className="field-row__label text-xs text-muted">{t('task.remindDate')}</label>
              <TextField
                type="datetime-local"
                value={task.remindDate ? isoToLocalParts(task.remindDate).date + 'T' + isoToLocalParts(task.remindDate).time : ''}
                onChange={(e) => commitField('remindDate')(e.target.value ? datetimeLocalToIso(e.target.value) : null)}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth size="small"
              />
            </div>
            <div className="fld">
              <label className="field-row__label text-xs text-muted">{t('task.queue')}</label>
              <TextField
                select value={task.queueId ?? ''}
                onChange={(e) => commitField('queueId')(e.target.value ? Number(e.target.value) : null)}
                fullWidth size="small"
              >
                <MenuItem value="">{t('common.none')}</MenuItem>
                {queues.map((q) => <MenuItem key={q.queueId} value={q.queueId}>{q.name}</MenuItem>)}
              </TextField>
            </div>
          </div>

          {/* Stato di pipeline: cascata pipeline -> stato, come nel form di creazione e in quello
              del preventivo. La pipeline NON si salva: e' solo il filtro della seconda tendina, e
              si parte da quella che possiede lo stato attuale del task.

              Riga propria e non accanto a promemoria/coda perche' le due tendine sono legate fra
              loro: cambiando pipeline lo stato scelto prima non esiste piu'. */}
          {taskPipelines.length > 0 && (
            <div className="tc-row2">
              <div className="fld">
                <label className="field-row__label text-xs text-muted">{t('task.pipeline')}</label>
                <TextField
                  select value={pipelineId || ''}
                  onChange={(e) => {
                    const next = e.target.value === '' ? 0 : Number(e.target.value);

                    setPipelineId(next);

                    // Cambiare pipeline TOGLIE la collocazione, e la scrive subito: lasciare a
                    // video uno stato dell'altra pipeline direbbe una cosa che il database non dice.
                    if (task.currentStateId) commitField('currentStateId')(0);
                  }}
                  fullWidth size="small"
                >
                  <MenuItem value="">{t('common.none')}</MenuItem>
                  {taskPipelines.map((pl) => (
                    <MenuItem key={pl.pipelineId} value={pl.pipelineId}>{pl.name}</MenuItem>
                  ))}
                </TextField>
              </div>
              <div className="fld">
                <label className="field-row__label text-xs text-muted">
                  {t('task.pipelineState')}
                </label>
                <TextField
                  select value={task.currentStateId || ''}
                  onChange={(e) =>
                    commitField('currentStateId')(e.target.value === '' ? 0 : Number(e.target.value))
                  }
                  disabled={pipelineId <= 0}
                  fullWidth size="small"
                >
                  <MenuItem value="">{t('common.none')}</MenuItem>
                  {pipelineStates.map((st) => (
                    <MenuItem key={st.pipelineStateId} value={st.pipelineStateId}>
                      {pipelineStateLabel(st)}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            </div>
          )}

          <div className="task-form__recurrence">
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={task.recurrent}
                  onChange={(e) => handleRecurrentUnitChange(
                    e.target.checked
                      ? (task.recurrentUnit && task.recurrentUnit !== 'none' ? task.recurrentUnit : 'day')
                      : 'none',
                  )}
                />
              }
              label={t('task.setRecurrence')}
            />
            {task.recurrent && (
              <div className="task-form__recurrence-expand">
                <TextField
                  type="number" label={t('task.recurrentValue')} value={task.recurrentValue}
                  onChange={(e) => saveTaskPatch({ recurrentValue: Number(e.target.value) || 1 })}
                  slotProps={{ htmlInput: { min: 1 } }}
                  className="form-field"
                />
                <TextField
                  select label={t('task.recurrentUnit')} value={task.recurrentUnit}
                  onChange={(e) => handleRecurrentUnitChange(e.target.value as TaskRecurrentUnit)}
                  fullWidth className="form-field"
                >
                  {TASK_RECURRENT_UNITS.map((u) => <MenuItem key={u.value} value={u.value}>{t(u.label)}</MenuItem>)}
                </TextField>
              </div>
            )}
          </div>

          <div className="form-field">
            <label className="field-row__label text-xs text-muted">{t('common.notes')}</label>
            <RichTextField value={task.notes} onCommit={commitField('notes')} placeholder={t('task.notesPlaceholder')} />
          </div>
        </div>
      </details>

      {/* Associazioni + commenti (come nell'editor inline): stesso ActivityTools. */}
      <div className="task-create__assoc">
        <ActivityTools
          {...recordsPickerProps}
          records={currentlyLinked}
          onChange={(val) => handleRecordsChange?.(val)}
          recordsSaving={recordsSaving}
          entityFqn={taskFqn ?? ''}
          entityId={task.taskId}
        />
      </div>
    </div>
  );
}
